import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import authenticationStore from '../stores/AuthenticationStore';
import userStore from '../stores/UserStore';
import { CircularProgress, Box } from '@mui/material';
import { useAppTheme } from '../hooks/useAppTheme';
import type { ThemedSxStyle } from '../types/themedStyles';
import { $loadingContainer } from '../theme/styles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = observer(({ 
  children, 
  redirectTo = '/loginscreen',
  requireAuth = true 
}) => {
  const location = useLocation();
  const { themed } = useAppTheme();

  // Check if user is authenticated
  // Allow demo users (userId === 'demo-user') even without Firebase auth
  const isDemoUser = userStore.user?.userId === 'demo-user';
  const isAuthenticated = (authenticationStore.isAuthenticated && userStore.user) || isDemoUser;

  // Skip the auth delay when the session is already live (SPA navigation).
  // On a full refresh, wait for Firebase or fall back to a short timeout.
  const [isCheckingAuth, setIsCheckingAuth] = useState(
    () => requireAuth && !isDemoUser && !isAuthenticated
  );

  const logRoute =
    location.pathname.includes('facilitator') || location.pathname.includes('workshop-manager');
  if (logRoute) {
    console.info('[OVH:WorkshopDash] ProtectedRoute:render', {
      path: location.pathname,
      requireAuth,
      isDemoUser,
      isAuthenticated,
      isCheckingAuth,
      hasAuthUser: !!authenticationStore.user,
      hasStoreUser: !!userStore.user,
      userRole: userStore.user?.userRole,
    });
  }

  // Always call hooks in the same order - move useEffect before any conditional returns
  useEffect(() => {
    if (isDemoUser || !requireAuth || isAuthenticated) {
      if (logRoute) {
        console.info('[OVH:WorkshopDash] ProtectedRoute:skip-delay', {
          path: location.pathname,
          isDemoUser,
          isAuthenticated,
        });
      }
      setIsCheckingAuth(false);
      return;
    }

    if (logRoute) {
      console.info('[OVH:WorkshopDash] ProtectedRoute:start-delay', { path: location.pathname });
    }
    const timer = setTimeout(() => {
      if (logRoute) {
        console.info('[OVH:WorkshopDash] ProtectedRoute:delay-elapsed', { path: location.pathname });
      }
      setIsCheckingAuth(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isDemoUser, requireAuth, isAuthenticated, location.pathname]);

  // If authentication is not required, render children
  if (!requireAuth) {
    return <>{children}</>;
  }

  // For demo users, skip the auth initialization delay and allow immediate access
  if (isDemoUser) {
    return <>{children}</>;
  }

  // Show loading spinner while checking authentication (only for non-demo users)
  if (isCheckingAuth) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        sx={themed([$loadingContainer, $loadingContainerBg])}
      >
        <CircularProgress sx={themed($progress)} />
      </Box>
    );
  }

  // If not authenticated, redirect to login with current location as state
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If authenticated, render the protected component
  return <>{children}</>;
});

const $loadingContainerBg: ThemedSxStyle = ({ colors }) => ({
  backgroundColor: colors.background,
  transition: 'background-color 0.3s ease'
});

const $progress: ThemedSxStyle = ({ colors }) => ({
  color: colors.palette.main
});

export default ProtectedRoute; 